import { jsx, jsxs } from 'react/jsx-runtime';
import { useContext, useState, useDeferredValue, useRef, useMemo, useCallback, useEffect } from 'react';
import { Checkbox, Stack, StackItem, CommandBarButton, Text, SearchBox, Callout, DirectionalHint, FocusZone, FocusZoneDirection, DefaultButton } from '@fluentui/react';
import classNames from 'classnames';
import Suggestions from './Suggestions/Suggestions.esm.js';
import { SuggestionTypes } from './Suggestions/types.esm.js';
import SuggestionContext from './Suggestions/SuggestionContext.esm.js';
import { saveSearchHistory, removeSearchHistory } from './searchHistory.esm.js';
import { flushSync } from 'react-dom';
import { useBreakpoint } from '@agoras/hooks';
import { styles } from './Searchbar.styles.esm.js';
import { useSearchQuery } from './hooks/useSearchQuery.esm.js';
import { useGallery } from './hooks/useGallery.esm.js';
import { useSuggestions } from './hooks/useSuggestions.esm.js';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../context/context.esm.js';
import logger from '@agoras/logger';
import { useId } from '@fluentui/react-hooks';

function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes(arr) {
    if (Array.isArray(arr)) return arr;
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _iterable_to_array_limit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _sliced_to_array(arr, i) {
    return _array_with_holes(arr) || _iterable_to_array_limit(arr, i) || _unsupported_iterable_to_array(arr, i) || _non_iterable_rest();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
var SEARCHBAR_CONTAINER_ID = 'agoras-searchbar-container';
var Searchbar = function(param) {
    var dropdownOptions = param.dropdownOptions, filters = param.filters, searchBoxProps = param.searchBoxProps, onSearch = param.onSearch, mfeContext = param.mfeContext, _param_showCallout = param.showCallout, showCallout = _param_showCallout === void 0 ? true : _param_showCallout, currentView = param.currentView;
    var _useContext = useContext(AppContext), navigate = _useContext.navigate, unifiedExperience = _useContext.unifiedExperience;
    var _useState = _sliced_to_array(useState(false), 2), forceUpdate = _useState[1];
    var _useState1 = _sliced_to_array(useState(false), 2), isSearching = _useState1[0], setIsSearching = _useState1[1];
    var _useBreakpoint = _sliced_to_array(useBreakpoint('sm', 'md'), 1), isMobile = _useBreakpoint[0];
    var _useBreakpoint1 = _sliced_to_array(useBreakpoint('md'), 1), isMedium = _useBreakpoint1[0];
    var _useState2 = _sliced_to_array(useState(true), 2), disabled = _useState2[0], setDisabled = _useState2[1];
    var _useState3 = _sliced_to_array(useState(''), 2), searchTerm = _useState3[0], setSearchTerm = _useState3[1];
    var deferredSearchTerm = useDeferredValue(searchTerm);
    var _useGallery = _sliced_to_array(useGallery(dropdownOptions, currentView), 2), gallery = _useGallery[0], setGallery = _useGallery[1];
    var _useState4 = _sliced_to_array(useState(false), 2), withCurerntFilters = _useState4[0], setWithCurrentFIlters = _useState4[1];
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    var t = useTranslation('search').t;
    var uniqueId = useId();
    var searchbarContainerId = "".concat(SEARCHBAR_CONTAINER_ID, "-").concat(uniqueId);
    var _useSuggestions = useSuggestions({
        searchTerm: deferredSearchTerm,
        gallery: gallery.gallery,
        mfeContext: mfeContext
    }), suggestions = _useSuggestions.suggestions, suggestionsRequestId = _useSuggestions.suggestionsRequestId;
    var shouldShowCallout = useDeferredValue(suggestions.length > 0 && !disabled && !isSearching && showCallout);
    useSearchQuery({
        mutation: function(term) {
            return setSearchTerm(term);
        }
    });
    var suggestionsFocusRef = useRef();
    var searchboxRef = useRef();
    var searchBoxPlaceholder = useMemo(function() {
        return t("Search ".concat(unifiedExperience ? 'Marketplace' : 'AppSource', " ").concat(disabled ? '' : gallery.text));
    }, [
        unifiedExperience,
        t,
        disabled,
        gallery.text
    ]);
    var handleEscapeClicked = function() {
        var _searchboxRef_current;
        (_searchboxRef_current = searchboxRef.current) === null || _searchboxRef_current === void 0 ? void 0 : _searchboxRef_current.blur();
        setDisabled(true);
    };
    var handleSearchBoxKeyDown = function(ev) {
        if (ev.key === 'ArrowDown') {
            var _suggestionsFocusRef_current;
            (_suggestionsFocusRef_current = suggestionsFocusRef.current) === null || _suggestionsFocusRef_current === void 0 ? void 0 : _suggestionsFocusRef_current.focus();
        }
    };
    var handleContainerClick = useCallback(function() {
        var _searchboxRef_current;
        if (!disabled) {
            if (isSearching) {
                setIsSearching(false);
            }
            return;
        }
        flushSync(function() {
            setDisabled(false);
        });
        (_searchboxRef_current = searchboxRef.current) === null || _searchboxRef_current === void 0 ? void 0 : _searchboxRef_current.focus();
    }, [
        disabled,
        isSearching
    ]);
    var disableSearchbar = useCallback(function() {
        setDisabled(true);
    }, []);
    var handleDismissSuggestions = useCallback(function() {
        setIsSearching(true);
    }, []);
    var handleSearch = useCallback(function(value) {
        if (!value) {
            return;
        }
        handleDismissSuggestions();
        saveSearchHistory(value);
        if (typeof onSearch !== 'function') {
            navigate({
                type: gallery.key,
                query: gallery.key === 'Partner' ? {
                    filter: "freetext=".concat(value)
                } : {
                    search: value
                }
            });
            return;
        }
        onSearch === null || onSearch === void 0 ? void 0 : onSearch({
            searchTerm: value,
            useCurrentFilters: withCurerntFilters,
            suggestions: suggestions !== null && suggestions !== void 0 ? suggestions : [],
            gallery: gallery.key,
            suggestionsRequestId: suggestionsRequestId
        });
    }, [
        navigate,
        gallery.key,
        onSearch,
        suggestions,
        handleDismissSuggestions,
        withCurerntFilters,
        suggestionsRequestId
    ]);
    var handleClickSearchButton = useCallback(function() {
        if (!(searchTerm === null || searchTerm === void 0 ? void 0 : searchTerm.length)) {
            return;
        }
        var logDetails = JSON.stringify({
            searchTerm: searchTerm
        });
        logger.info(logDetails, _object_spread_props(_object_spread({}, mfeContext), {
            action: 'Click',
            actionModifier: 'Search Button'
        }));
        handleSearch(searchTerm);
    }, [
        searchTerm,
        handleSearch
    ]);
    var handleSearchEnterPressed = useCallback(function(param) {
        var searchTerm = param.searchTerm;
        if (!(searchTerm === null || searchTerm === void 0 ? void 0 : searchTerm.length)) {
            return;
        }
        var logDetails = JSON.stringify({
            searchTerm: searchTerm
        });
        logger.info(logDetails, _object_spread_props(_object_spread({}, mfeContext), {
            action: 'Press',
            actionModifier: 'Search Enter'
        }));
        handleSearch(searchTerm);
    }, [
        handleSearch
    ]);
    var handleClickTermSuggestion = useCallback(function(item) {
        var rank = suggestions.findIndex(function(suggestion) {
            return suggestion === item;
        }) + 1;
        var total = suggestions.length;
        var logDetails = JSON.stringify({
            searchTerm: item.displayText,
            suggestionType: item.suggestionType,
            rank: rank,
            total: total,
            suggestionsRequestId: suggestionsRequestId
        });
        logger.info(logDetails, _object_spread_props(_object_spread({}, mfeContext), {
            action: 'Click',
            actionModifier: 'Term Search Suggestions'
        }));
        setSearchTerm(item.displayText);
        handleSearch(item.displayText);
    }, [
        suggestions,
        handleSearch,
        suggestionsRequestId
    ]);
    var handleClickSuggestion = useCallback(function(item) {
        if (item.suggestionType !== SuggestionTypes.Entity && item.suggestionType !== SuggestionTypes.Partner) {
            handleClickTermSuggestion(item);
            return;
        }
        logger.info(JSON.stringify({
            searchTerm: deferredSearchTerm,
            item: item,
            suggestionsRequestId: suggestionsRequestId
        }), _object_spread_props(_object_spread({}, mfeContext), {
            action: 'Search',
            actionModifier: 'App Search Suggestions',
            appName: item.id
        }));
    }, [
        deferredSearchTerm,
        handleClickTermSuggestion,
        suggestionsRequestId
    ]);
    var handleRemoveHistoryItem = useCallback(function(historyItem) {
        removeSearchHistory(historyItem);
        forceUpdate(function(oldVal) {
            return !oldVal;
        });
    }, []);
    var handleFirstSuggestionKeyDown = useCallback(function(ev) {
        if (ev.key === 'ArrowUp') {
            var _searchboxRef_current;
            (_searchboxRef_current = searchboxRef.current) === null || _searchboxRef_current === void 0 ? void 0 : _searchboxRef_current.focus();
        }
    }, []);
    useEffect(function() {
        if (!disabled) {
            window.addEventListener('click', disableSearchbar, {
                once: true
            });
        }
        return function() {
            window.removeEventListener('click', disableSearchbar);
        };
    }, [
        disableSearchbar,
        disabled
    ]);
    var memoizedSuggestions = useMemo(function() {
        if (!suggestions) {
            return null;
        }
        var hasFilters = Boolean(filters === null || filters === void 0 ? void 0 : filters.length);
        var labelText = t('Search within current filters', {
            filtersCount: filters === null || filters === void 0 ? void 0 : filters.length
        });
        return /*#__PURE__*/ jsx(SuggestionContext.Provider, {
            value: {
                onRemoveHistoryItem: handleRemoveHistoryItem,
                onDismissSuggestions: handleDismissSuggestions,
                onFirstSuggestionKeyDown: handleFirstSuggestionKeyDown,
                onClickSuggestionItem: handleClickSuggestion
            },
            children: /*#__PURE__*/ jsx(Suggestions, {
                searchTerm: deferredSearchTerm,
                items: suggestions,
                children: hasFilters && /*#__PURE__*/ jsx(Checkbox, {
                    checked: withCurerntFilters,
                    onChange: function() {
                        setWithCurrentFIlters(!withCurerntFilters);
                    },
                    ariaLabel: "search with filters",
                    className: styles.checkbox,
                    inputProps: {
                        onKeyDown: handleFirstSuggestionKeyDown
                    },
                    label: labelText
                })
            })
        });
    }, [
        deferredSearchTerm,
        filters === null || filters === void 0 ? void 0 : filters.length,
        withCurerntFilters,
        suggestions,
        handleClickSuggestion,
        handleDismissSuggestions,
        handleFirstSuggestionKeyDown,
        handleRemoveHistoryItem,
        t
    ]);
    return /*#__PURE__*/ jsxs(Stack, {
        className: styles.container,
        onClick: function(e) {
            e.stopPropagation();
        },
        horizontal: true,
        grow: true,
        children: [
            /*#__PURE__*/ jsx(StackItem, {
                children: /*#__PURE__*/ jsx(CommandBarButton, {
                    className: styles.dropdown,
                    checked: true,
                    ariaLabel: "gallery picker",
                    menuProps: {
                        isBeakVisible: false,
                        items: dropdownOptions.map(function(option) {
                            return _object_spread_props(_object_spread({}, option), {
                                text: t(option.text)
                            });
                        }),
                        onItemClick: function(_, item) {
                            if ((item === null || item === void 0 ? void 0 : item.key) && item.text) {
                                var key = item.key, text = item.text, gallery = item.gallery;
                                setGallery({
                                    key: key,
                                    text: text,
                                    gallery: gallery !== null && gallery !== void 0 ? gallery : key
                                });
                            }
                        }
                    },
                    persistMenu: true,
                    children: /*#__PURE__*/ jsx(Text, {
                        className: styles.dropdownText,
                        children: t(gallery.text)
                    })
                })
            }),
            /*#__PURE__*/ jsx(StackItem, {
                id: searchbarContainerId,
                onClickCapture: handleContainerClick,
                onKeyDown: handleContainerClick,
                tabIndex: 0,
                className: classNames(styles.searchbarContainer, searchBoxProps === null || searchBoxProps === void 0 ? void 0 : searchBoxProps.className),
                grow: true,
                children: /*#__PURE__*/ jsxs("form", {
                    role: "search",
                    onSubmit: function(e) {
                        e.preventDefault();
                    },
                    children: [
                        /*#__PURE__*/ jsx(SearchBox, {
                            showIcon: true,
                            autoComplete: "none",
                            disabled: disabled,
                            "aria-disabled": false,
                            placeholder: searchBoxPlaceholder,
                            value: searchTerm,
                            onSearch: function(searchTerm) {
                                return handleSearchEnterPressed({
                                    searchTerm: searchTerm
                                });
                            },
                            onClear: function() {
                                setSearchTerm('');
                            },
                            onKeyDown: handleSearchBoxKeyDown,
                            onEscape: handleEscapeClicked,
                            onChange: function(_, value) {
                                setSearchTerm(value !== null && value !== void 0 ? value : '');
                                if (isSearching) {
                                    setIsSearching(false);
                                }
                            },
                            componentRef: function(ref) {
                                searchboxRef.current = ref;
                            },
                            clearButtonProps: {
                                className: classNames(styles.icon, _define_property({}, styles.hidden, disabled)),
                                iconProps: {
                                    iconName: 'Clear',
                                    title: 'Clear'
                                }
                            },
                            styles: {
                                root: {
                                    borderRadius: '0px'
                                }
                            }
                        }),
                        /*#__PURE__*/ jsx(Callout, {
                            isBeakVisible: false,
                            target: "#".concat(searchbarContainerId),
                            className: styles.callout,
                            hidden: !shouldShowCallout,
                            doNotLayer: !isMobile,
                            directionalHint: DirectionalHint.bottomCenter,
                            directionalHintForRTL: DirectionalHint.bottomCenter,
                            minPagePadding: isMedium ? 16 : undefined,
                            children: /*#__PURE__*/ jsx(FocusZone, {
                                className: styles.focusZone,
                                componentRef: function(ref) {
                                    suggestionsFocusRef.current = ref;
                                },
                                direction: FocusZoneDirection.vertical,
                                children: memoizedSuggestions
                            })
                        }),
                        /*#__PURE__*/ jsx(DefaultButton, {
                            title: "Search",
                            onClick: handleClickSearchButton,
                            target: searchbarContainerId,
                            ariaLabel: "search button",
                            className: classNames(styles.button, _define_property({}, styles.hidden, disabled)),
                            primary: true,
                            iconProps: {
                                iconName: 'Forward'
                            }
                        })
                    ]
                })
            })
        ]
    });
};

export { Searchbar as default };
